import { atom } from 'jotai'
import { api, FieldError } from '../lib/api'

export type WaitlistStatus = 'unknown' | 'checking' | 'active' | 'waitlisted'

export type RegionCheckResult = {
  isActive: boolean
  region?: string
}

export type WaitlistEntry = {
  id: string
  email: string
  location: string
  notifiedAt?: string
  createdAt: string
}

export type LocationData = {
  osmId: number
  osmType: string
  latitude: number
  longitude: number
  displayName: string
  country: string
}

export const waitlistStatusAtom = atom<WaitlistStatus>('unknown')

export const waitlistLocationAtom = atom<LocationData | null>(null)

export const regionCheckResultAtom = atom<RegionCheckResult | null>(null)

export const waitlistEntryAtom = atom<WaitlistEntry | null>(null)

export const waitlistIsLoadingAtom = atom(false)

export const waitlistIssuesAtom = atom<FieldError[]>([])

export const checkRegionStatusAtom = atom(
  null,
  async function checkRegionStatus(_get, set, location: LocationData) {
    set(waitlistIsLoadingAtom, true)
    set(waitlistIssuesAtom, [])
    set(waitlistStatusAtom, 'checking')
    set(waitlistLocationAtom, location)

    try {
      const response = await api.fetch('/waitlist/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          osmId: location.osmId,
          osmType: location.osmType,
        }),
      })

      if (!response.ok) {
        const data = (await response.json()) as any
        set(waitlistIssuesAtom, data.fields ?? [{ location: 'checkFailed' }])
        set(waitlistStatusAtom, 'unknown')
        set(waitlistIsLoadingAtom, false)
        return { success: false }
      }

      const data = (await response.json()) as RegionCheckResult
      set(regionCheckResultAtom, data)
      set(waitlistStatusAtom, data.isActive ? 'active' : 'waitlisted')
      set(waitlistIsLoadingAtom, false)
      return { success: true, isActive: data.isActive }
    } catch (_err) {
      set(waitlistIssuesAtom, [{ location: 'networkError' }])
      set(waitlistStatusAtom, 'unknown')
      set(waitlistIsLoadingAtom, false)
      return { success: false }
    }
  },
)

export const joinWaitlistAtom = atom(null, async function joinWaitlist(get, set, email: string) {
  const location = get(waitlistLocationAtom)
  if (!location) {
    set(waitlistIssuesAtom, [{ location: 'required' }])
    return { success: false }
  }

  set(waitlistIsLoadingAtom, true)
  set(waitlistIssuesAtom, [])

  try {
    const response = await api.fetch('/waitlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        osmId: location.osmId,
        osmType: location.osmType,
      }),
    })

    if (!response.ok) {
      const data = (await response.json()) as any
      set(waitlistIssuesAtom, data.fields ?? data ?? [{ email: 'joinFailed' }])
      set(waitlistIsLoadingAtom, false)
      return { success: false }
    }

    const data = (await response.json()) as WaitlistEntry
    set(waitlistEntryAtom, data)
    set(waitlistIsLoadingAtom, false)
    return { success: true }
  } catch (_err) {
    set(waitlistIssuesAtom, [{ email: 'networkError' }])
    set(waitlistIsLoadingAtom, false)
    return { success: false }
  }
})

export const resetWaitlistAtom = atom(null, function resetWaitlist(_get, set) {
  set(waitlistStatusAtom, 'unknown')
  set(waitlistLocationAtom, null)
  set(regionCheckResultAtom, null)
  set(waitlistEntryAtom, null)
  set(waitlistIssuesAtom, [])
})
